/** Two activities' state, joined into one board by the cell id they both use. */

import { hexgrid } from "@nebulize/sdk";

import { currentAttribute, has, type CanvasState, type StateEntity } from "../../lib/runtime";
import type { HexState } from "./HexBoard";

const SELECTED = "activity.hex_board.selected";
const BLOCKED = "activity.hex_board.blocked";

/** The grid the ids describe: even rows hold `columns`, odd rows one more. */
export function boardSize(entities: StateEntity[]): { columns: number; rows: number } {
  let rows = 0;
  let columns = 0;
  for (const entity of entities) {
    const cell = hexgrid.cellFromId(entity.id);
    if (cell === null) continue;
    rows = Math.max(rows, cell.row + 1);
    if (cell.row % 2 === 0) columns = Math.max(columns, cell.column + 1);
  }
  return { columns, rows };
}

/** One entry per hex: the tally from the choice board, the state from the game board. */
export function boardCells(
  choices: CanvasState | null,
  game: CanvasState | null,
): Map<string, HexState> {
  const cells = new Map<string, HexState>();
  const of = (id: string): HexState =>
    cells.get(id) ?? { picks: 0, selected: false, blocked: false };

  for (const entity of choices?.entities ?? []) {
    if (hexgrid.cellFromId(entity.id) === null) continue;
    cells.set(entity.id, { ...of(entity.id), picks: currentAttribute(entity, "picks") });
  }
  for (const entity of game?.entities ?? []) {
    if (hexgrid.cellFromId(entity.id) === null) continue;
    cells.set(entity.id, {
      ...of(entity.id),
      selected: has(entity, SELECTED),
      blocked: has(entity, BLOCKED),
    });
  }
  return cells;
}

/** How many picks the board holds in total, which a reset takes back to zero. */
export function totalPicks(cells: Map<string, HexState>): number {
  let total = 0;
  for (const one of cells.values()) total += one.picks;
  return total;
}
